import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTenant } from '../../context/TenantContext';
import { patientService, Patient } from '../../services/patientService';
import { meetingTypeService, MeetingType } from '../../services/meetingTypeService';
import { clinicService, Clinic } from '../../services/clinicService';
import { yomanService } from '../../services/yomanService';
import { Meeting } from '../../services/meetingService';
import { PatientFormDialog } from '../dialogs/PatientFormDialog';
import { YomanSchedulingDialog } from '../dialogs/YomanSchedulingDialog';
import { CMSummaryModal } from '../modals/CMSummaryModal';
import { DoctorSummaryModal } from '../modals/DoctorSummaryModal';

export type PatientAction = 'edit_patient' | 'set_meeting' | null;

interface PatientActionDialogsProps {
  patientId: number;
  action: PatientAction;
  summaryMeeting: Meeting | null;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Self-contained host for the patient-context action modals (Edit Patient,
 * Set Meeting, Meeting Summary). Loads the patient internally so these modals
 * can be opened from anywhere (e.g. the task action button) and layer on top
 * of whatever is already open — without navigating to the patient page.
 */
const PatientActionDialogs: React.FC<PatientActionDialogsProps> = ({
  patientId, action, summaryMeeting, onClose, onSaved,
}) => {
  const { currentTenant } = useTenant();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [yomanLoading, setYomanLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ id_number?: string }>({});

  const { register, handleSubmit, formState: { errors, submitCount }, reset, control } = useForm({ mode: 'onChange' });

  const active = action !== null || summaryMeeting !== null;

  useEffect(() => {
    if (!active || !currentTenant) return;
    let cancelled = false;
    (async () => {
      try {
        const [p, mts, cls] = await Promise.all([
          patientService.getPatient(patientId),
          meetingTypeService.getMeetingTypes(currentTenant.id),
          clinicService.getClinics(currentTenant.id),
        ]);
        if (cancelled) return;
        setPatient(p);
        setMeetingTypes(mts);
        setClinics(cls);
        if (action === 'edit_patient') {
          reset({
            first_name: p.first_name,
            last_name: p.last_name,
            email: p.email || '',
            phone: p.phone || '',
            date_of_birth: p.date_of_birth,
            id_number: p.id_number || '',
            gender: p.gender || '',
            additional_fields: p.additional_fields || {},
          });
        }
      } catch {
        if (!cancelled) { toast.error('Failed to load patient'); onClose(); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, patientId, currentTenant, action]);

  const clinic = clinics.find((c) => c.id === patient?.clinic_id) || clinics[0];
  const defaultCountryCode = clinic?.default_country_code;
  const clinicState = clinic?.state || null;

  const handleSavePatient = async (data: any) => {
    if (!patient) return;
    try {
      await patientService.updatePatient(patient.id, data);
      toast.success('Patient updated');
      onSaved?.();
      onClose();
    } catch (err: any) {
      const fullError = JSON.stringify(err.response?.data || '').toLowerCase();
      if (err.response?.status === 400 && (fullError.includes('id number') || fullError.includes('id_number'))) {
        setFieldErrors({ id_number: 'ID Number / NHS Number already exists' });
      } else {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Failed to update patient');
      }
    }
  };

  const handleYomanCreate = async (meetingTypeId: number, therapistId?: number) => {
    if (!patient) return;
    try {
      setYomanLoading(true);
      const res = await yomanService.createInvite({
        patient_id: patient.id,
        meeting_type_id: meetingTypeId,
        therapist_id: therapistId,
      });
      window.open(res.invite_url, '_blank', 'noopener,noreferrer');
      toast.success('Yoman meeting invite created');
      onClose();
    } catch {
      toast.error('Failed to create Yoman meeting invite');
    } finally {
      setYomanLoading(false);
    }
  };

  if (!active) return null;

  const isCM = !!summaryMeeting && (
    summaryMeeting.meeting_type?.type === 'case_manager_intake' ||
    summaryMeeting.meeting_type?.type === 'case_manager_followup'
  );

  return (
    <>
      {action === 'edit_patient' && patient && (
        <PatientFormDialog
          open
          onClose={onClose}
          onSubmit={handleSubmit(handleSavePatient)}
          editingPatient={patient}
          register={register}
          errors={errors}
          submitCount={submitCount}
          control={control}
          defaultCountryCode={defaultCountryCode}
          clinicState={clinicState}
          fieldErrors={fieldErrors}
          onClearFieldError={(field) => setFieldErrors((prev) => { const next = { ...prev }; delete next[field]; return next; })}
          patientTypeId={patient.patient_type_id ?? undefined}
        />
      )}

      {action === 'set_meeting' && patient && (
        <YomanSchedulingDialog
          open
          onClose={onClose}
          patient={patient}
          meetingTypes={meetingTypes}
          loading={yomanLoading}
          currentTenantId={currentTenant?.id}
          onCreateInvite={handleYomanCreate}
        />
      )}

      {summaryMeeting && isCM && (
        <CMSummaryModal
          isOpen
          onClose={onClose}
          meeting={summaryMeeting}
          onSuccess={() => onSaved?.()}
        />
      )}
      {summaryMeeting && !isCM && (
        <DoctorSummaryModal
          isOpen
          onClose={onClose}
          meeting={summaryMeeting}
          onSuccess={() => onSaved?.()}
          clinicLanguage={clinic?.language}
          clinicState={clinicState}
        />
      )}
    </>
  );
};

export default PatientActionDialogs;
